import { debounce } from "lodash/fp";
import React, { useRef, useState } from "react";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import OverwatchModal from "@/components/OverwatchModal";
import { CreateUserDocument, GetExistingUsersEmailsDocument } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import { isValidEmail } from "@/utils/userInformation";
import { DEFAULT_USER_DATA } from "@/utils/constants";
import UserFormFields from "./UserFormFields";
import { omit } from "lodash";
import { Checkbox, FormControlLabel } from "@mui/material";

const normalDebounce = debounce(500);

interface CreateUserModalProps {
  handleClose: () => void;
  onSuccess?: () => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ handleClose, onSuccess }) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [user, setUser] = useState(DEFAULT_USER_DATA);
  const [toggleMultiple, setToggleMultiple] = useState(false);
  const [resetIndex, setResetIndex] = useState(0);

  const [createUser] = useMutation(CreateUserDocument, {
    onCompleted: () => {
      if (!toggleMultiple) handleClose();
      if (toggleMultiple) {
        debounceFunc.cancel();
        setUser(DEFAULT_USER_DATA);
        setResetIndex((prev) => prev + 1);
      }
      displayNotification({ type: "success", text: "User Created Successfully" });
      onSuccess?.();
    },
    onError: () => displayNotification({ type: "error", text: "Unable to create user, try again later." }),
    refetchQueries: ["getAllEmployees", "getManagers", "getNewUsersRequests", "getUserNotifications"],
  });

  const [fetchEmails, { data }] = useLazyQuery(GetExistingUsersEmailsDocument, {
    nextFetchPolicy: 'network-only'
  });

  const handleOnChange = (key: string, value: string) => {
    debounceFunc.cancel();
    debounceFunc(() => {
      setUser((prev) => ({
        ...prev,
        [key]: value,
      }));
    });

    if (key === 'firstName' || key === 'lastName') {
      fetchEmails({
        variables: {
          firstName: key === 'firstName' ? value : user.firstName,
          lastName: key === 'lastName' ? value : user.lastName
        }
      });
    }
  };

  const handleSubmit = () => {
    createUser({ variables: user });
  };

  const disableCreate = () => Object.values(omit(user, "primaryPhone")).some((val) => val === "") || !isValidEmail(user.email);

  return (
    <OverwatchModal
      size="sm"
      title="Add a new person"
      handleClose={handleClose}
      primaryButtonText="Create"
      primaryButtonAction={handleSubmit}
      disablePrimary={disableCreate()}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
    >
      <UserFormFields
        key={resetIndex}
        user={user}
        existingEmails={data?.getExistingUsersEmails}
        onChange={handleOnChange}
      />
      <FormControlLabel
        label="Add multiple users"
        control={
          <Checkbox
            checked={toggleMultiple}
            onChange={() => setToggleMultiple((prev) => !prev)}
          />
        }
      />
    </OverwatchModal>
  );
};

export default CreateUserModal;
